import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";

const initial = {
  name: "",
  email: "",
  phone: "",
  city: "",
  branch: "",
  motorcycle_type: "",
  experience: "",
  message: "",
};

export default function JoinForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.branch) {
      toast.error("Please select a branch to apply to (MC, SC, or Undecided).");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/prospect", form);
      toast.success("Application received! A sister will reach out soon.");
      setDone(true);
      setForm(initial);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      id="join"
      className="relative py-24 sm:py-32 border-t border-white/5 bg-[#080808]"
      data-testid="join-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-16">
        {/* Left */}
        <div className="lg:col-span-5 lg:sticky lg:top-28 h-fit">
          <div className="text-xs uppercase tracking-[0.35em] text-brand mb-4 flex items-center gap-3">
            <span className="h-px w-10 bg-brand" /> Prospect Application
          </div>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-serif leading-[1.02] mb-8"
            data-testid="join-title"
          >
            Ready to earn
            <br />
            <span className="italic text-brand">your crown?</span>
          </h2>
          <p className="text-white/70 leading-relaxed mb-8 max-w-md">
            Crown Jewels isn't a membership — it's a commitment. Share a little
            about yourself and we'll be in touch to invite you to meet the
            sisterhood in person.
          </p>
          <ul className="space-y-3 text-sm text-white/60">
            {[
              "Houston-based or anywhere in the U.S. — sisters ride in multiple states",
              "In good standing with no affiliations to rival clubs",
              "Open heart, strong mind, loyal spirit",
            ].map((t, i) => (
              <li key={i} className="flex gap-3" data-testid={`join-req-${i}`}>
                <Crown className="w-4 h-4 text-brand mt-0.5 shrink-0" />
                {t}
              </li>
            ))}
          </ul>
          <p className="mt-10 font-script text-3xl text-brand">
            The crown is calling.
          </p>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7" data-testid="join-form-wrapper">
          {done ? (
            <div
              className="relative bg-[#0a0a0a] border border-brand/40 p-12 text-center shadow-brand-lg"
              data-testid="join-success"
            >
              <Crown className="w-10 h-10 text-brand mx-auto mb-5" />
              <h3 className="text-3xl font-serif text-white mb-3">
                Welcome, future queen.
              </h3>
              <p className="text-white/70 max-w-md mx-auto">
                Your application has been received. A Crown Jewels sister will
                reach out to <strong className="text-white">{BRAND.city}</strong>-area
                prospects within 7 days. Keep your crown polished. 👑
              </p>
              <Button
                onClick={() => setDone(false)}
                variant="ghost"
                className="mt-8 text-brand hover:text-brand-hover hover:bg-transparent"
                data-testid="join-reset-btn"
              >
                Submit another application
              </Button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="relative bg-[#0a0a0a] border border-white/10 p-6 sm:p-10 space-y-6"
              data-testid="join-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field
                  label="Full name"
                  required
                  id="name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your royal name"
                  testId="join-name-input"
                />
                <Field
                  label="Email"
                  required
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="queen@email.com"
                  testId="join-email-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field
                  label="Phone"
                  required
                  id="phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="(713) 555-0100"
                  testId="join-phone-input"
                />
                <Field
                  label="City & State"
                  required
                  id="city"
                  value={form.city}
                  onChange={update("city")}
                  placeholder="Houston, TX · Dallas, TX · Atlanta, GA…"
                  testId="join-city-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.25em] text-white/70">
                  Which branch? *
                </Label>
                <Select
                  value={form.branch}
                  onValueChange={(v) => setForm((p) => ({ ...p, branch: v }))}
                >
                  <SelectTrigger
                    className="bg-[#050505] border-white/15 rounded-none h-12 focus:ring-brand focus:ring-1 focus:border-brand text-white"
                    data-testid="join-branch-trigger"
                  >
                    <SelectValue placeholder="Select a branch" />
                  </SelectTrigger>
                  <SelectContent
                    className="bg-[#0a0a0a] border-white/15 rounded-none text-white"
                    data-testid="join-branch-content"
                  >
                    <SelectItem value="MC" data-testid="join-branch-mc">
                      Motorcycle Club (MC) · Riders
                    </SelectItem>
                    <SelectItem value="SC" data-testid="join-branch-sc">
                      Social Club (SC) · Supporters
                    </SelectItem>
                    <SelectItem value="Undecided" data-testid="join-branch-undecided">
                      Undecided / Tell me more
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="motorcycle_type"
                  className="text-xs uppercase tracking-[0.25em] text-white/70"
                >
                  Type of motorcycle you ride
                </Label>
                <Input
                  id="motorcycle_type"
                  value={form.motorcycle_type}
                  onChange={update("motorcycle_type")}
                  placeholder="e.g. Harley Street Glide, Indian Scout, Tri Glide, Kawasaki Ninja, or 'N/A — Social Club'"
                  className="bg-[#050505] border-white/15 rounded-none h-12 focus-visible:ring-brand focus-visible:ring-1 focus-visible:border-brand text-white"
                  data-testid="join-motorcycle-input"
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="experience"
                  className="text-xs uppercase tracking-[0.25em] text-white/70"
                >
                  Riding / club experience *
                </Label>
                <Textarea
                  id="experience"
                  required
                  value={form.experience}
                  onChange={update("experience")}
                  placeholder="Years riding, bike you own, previous club affiliations, etc."
                  rows={4}
                  className="bg-[#050505] border-white/15 rounded-none focus-visible:ring-brand focus-visible:ring-1 focus-visible:border-brand text-white resize-none"
                  data-testid="join-experience-input"
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="message"
                  className="text-xs uppercase tracking-[0.25em] text-white/70"
                >
                  What does the crown (a.k.a. sisterhood) mean to you?
                </Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Every Crown Jewels sister answers this. Share what the crown means to you in your own words."
                  rows={5}
                  className="bg-[#050505] border-white/15 rounded-none focus-visible:ring-brand focus-visible:ring-1 focus-visible:border-brand text-white resize-none"
                  data-testid="join-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-14 bg-brand hover:bg-brand-hover text-white rounded-none text-sm font-semibold uppercase tracking-[0.2em] shadow-brand group"
                data-testid="join-submit-btn"
              >
                {submitting ? "Submitting..." : "Send Application"}
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, id, type = "text", value, onChange, placeholder, testId }) {
  return (
    <div className="space-y-2">
      <Label
        htmlFor={id}
        className="text-xs uppercase tracking-[0.25em] text-white/70"
      >
        {label}
        {required && " *"}
      </Label>
      <Input
        id={id}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="bg-[#050505] border-white/15 rounded-none h-12 focus-visible:ring-brand focus-visible:ring-1 focus-visible:border-brand text-white"
        data-testid={testId}
      />
    </div>
  );
}
